/** AG Grid text filter operators supported on the commercial board (community text filter). */
export const COMMERCIAL_TEXT_FILTER_TYPES = [
  { id: "contains", label: "Contains" },
  { id: "notContains", label: "Does not contain" },
  { id: "equals", label: "Equals" },
  { id: "notEqual", label: "Does not equal" },
  { id: "startsWith", label: "Starts with" },
  { id: "endsWith", label: "Ends with" },
];

const VALID_TYPES = new Set(COMMERCIAL_TEXT_FILTER_TYPES.map((t) => t.id));

export function normalizeCommercialGridDefaults(raw) {
  if (!raw || typeof raw !== "object") {
    return { enabled: false, rules: [], roleDefaults: [] };
  }
  const enabled = Boolean(raw.enabled);
  const rules = normalizeCommercialGridFilterRules(raw.rules);
  const roleDefaults = Array.isArray(raw.roleDefaults)
    ? raw.roleDefaults
        .map((entry) => ({
          userTypeId: String(entry?.userTypeId || "").trim(),
          enabled: Boolean(entry?.enabled),
          rules: normalizeCommercialGridFilterRules(entry?.rules),
        }))
        .filter((entry) => entry.userTypeId)
    : [];
  return { enabled, rules, roleDefaults };
}

function normalizeCommercialGridFilterRules(rawRules) {
  if (!Array.isArray(rawRules)) return [];
  return rawRules
    .map((r) => ({
      colId: String(r?.colId || "").trim(),
      type: VALID_TYPES.has(String(r?.type || "").trim()) ? String(r.type).trim() : "contains",
      filter: String(r?.filter ?? "").trim(),
    }))
    .filter((r) => r.colId && r.filter);
}

/** Convert admin rules into an AG Grid filter model (one or more conditions per column). */
export function rulesToFilterModel(rules) {
  const byCol = new Map();
  for (const r of rules || []) {
    if (!r?.colId || !String(r.filter ?? "").trim()) continue;
    const type = VALID_TYPES.has(r.type) ? r.type : "contains";
    if (!byCol.has(r.colId)) byCol.set(r.colId, []);
    byCol.get(r.colId).push({ type, filter: String(r.filter).trim() });
  }
  const filterModel = {};
  for (const [colId, conditions] of byCol) {
    if (conditions.length === 1) {
      filterModel[colId] = {
        filterType: "text",
        type: conditions[0].type,
        filter: conditions[0].filter,
      };
    } else {
      filterModel[colId] = {
        filterType: "text",
        operator: "AND",
        conditions,
      };
    }
  }
  return Object.keys(filterModel).length ? filterModel : null;
}

/** Best-effort reverse for loading saved settings into the admin editor. */
export function filterModelToRules(filterModel) {
  if (!filterModel || typeof filterModel !== "object") return [];
  const rules = [];
  for (const [colId, model] of Object.entries(filterModel)) {
    if (!model || typeof model !== "object") continue;
    if (Array.isArray(model.conditions)) {
      for (const c of model.conditions) {
        const filter = String(c?.filter ?? "").trim();
        if (!filter) continue;
        rules.push({
          colId,
          type: VALID_TYPES.has(c?.type) ? c.type : "contains",
          filter,
        });
      }
    } else {
      const filter = String(model.filter ?? "").trim();
      if (!filter) continue;
      rules.push({
        colId,
        type: VALID_TYPES.has(model.type) ? model.type : "contains",
        filter,
      });
    }
  }
  return rules;
}

/**
 * @param {object} defaults
 * @param {string|null} userTypeId
 * @param {{ matchingUserTypeIds?: Iterable<string>|null }} [options]
 *   When set, role defaults match any configured userTypeId in this set
 *   (exact viewer id plus persona-equivalent / seed ids).
 */
export function getCommercialGridDefaultFilterModel(defaults, userTypeId = null, options = {}) {
  const normalized = normalizeCommercialGridDefaults(defaults);
  const combined = [];
  if (normalized.enabled && normalized.rules.length) {
    combined.push(...normalized.rules);
  }
  const matchingIds = new Set();
  if (userTypeId) matchingIds.add(String(userTypeId));
  if (options?.matchingUserTypeIds) {
    for (const id of options.matchingUserTypeIds) {
      if (id) matchingIds.add(String(id));
    }
  }
  if (matchingIds.size) {
    const roleEntry = normalized.roleDefaults.find(
      (entry) => entry.enabled && entry.rules.length && matchingIds.has(entry.userTypeId)
    );
    if (roleEntry) combined.push(...roleEntry.rules);
  }
  if (!combined.length) return null;
  return rulesToFilterModel(combined);
}

/**
 * @param {object} defaults
 * @param {string|null} userTypeId
 * @param {{ matchingUserTypeIds?: Iterable<string>|null }} [options]
 */
export function commercialGridDefaultsActiveForViewer(defaults, userTypeId = null, options = {}) {
  const normalized = normalizeCommercialGridDefaults(defaults);
  if (normalized.enabled && normalized.rules.length) return true;
  const matchingIds = new Set();
  if (userTypeId) matchingIds.add(String(userTypeId));
  if (options?.matchingUserTypeIds) {
    for (const id of options.matchingUserTypeIds) {
      if (id) matchingIds.add(String(id));
    }
  }
  if (!matchingIds.size) return false;
  return normalized.roleDefaults.some(
    (entry) => entry.enabled && entry.rules.length && matchingIds.has(entry.userTypeId)
  );
}
